import React, { useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  Switch,
  Chip,
  TextField
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import AddIcon from "@mui/icons-material/Add";
import Button from "@mui/material/Button";

import { FormInputText } from "../ui/form/TextInput";
import { FormInputDropdown } from "../ui/form/Select";
import { CriteriaBuilder } from "./components/CriteriaBuilder";
import {
  DIRECTION_OPTIONS,
  TYPE_OPTIONS
} from "../../constants/spendingAggregations";
import {
  useCreateSpendingAggregation,
  useUpdateSpendingAggregation,
  useArchiveSpendingAggregation,
  useRestoreSpendingAggregation
} from "./hooks/useSpendingAggregationActions";

const validationSchema = yup.object({
  name: yup.string().required("Name is required"),
  direction: yup
    .string()
    .oneOf(DIRECTION_OPTIONS.map((option) => option.value), "Invalid direction")
    .required("Direction is required"),
  type: yup
    .string()
    .oneOf(TYPE_OPTIONS.map((option) => option.value), "Invalid type")
    .required("Type is required"),
  description: yup.string().nullable(),
  icon: yup.string().nullable(),
  isFeatured: yup.boolean()
}).required();

const emptyAggregation = {
  name: "",
  direction: "reduce",
  type: "standard",
  description: "",
  icon: "",
  criteria: {},
  hidden: false,
  isFeatured: false,
  shouldPreview: false,
  previewIconUrls: []
};

export const SpendingAggregationDrawer = ({ aggregation, isOpen, onClose }) => {
  const isEditing = Boolean(aggregation?.id);
  const [criteria, setCriteria] = useState(
    aggregation?.criteria ?? emptyAggregation.criteria
  );
  const [previewIconUrls, setPreviewIconUrls] = useState(
    aggregation?.previewIconUrls ?? []
  );
  const [newIconUrl, setNewIconUrl] = useState("");

  const { createSpendingAggregation } = useCreateSpendingAggregation();
  const { updateSpendingAggregation } = useUpdateSpendingAggregation();
  const { archiveSpendingAggregation } = useArchiveSpendingAggregation();
  const { restoreSpendingAggregation } = useRestoreSpendingAggregation();

  const {
    control,
    setValue,
    handleSubmit,
    formState: { isSubmitting }
  } = useForm({
    defaultValues: { ...emptyAggregation, ...aggregation },
    resolver: yupResolver(validationSchema)
  });

  const addIconUrl = () => {
    const trimmed = newIconUrl.trim();
    if (!trimmed || previewIconUrls.includes(trimmed)) {
      return;
    }
    setPreviewIconUrls((prev) => [...prev, trimmed]);
    setNewIconUrl("");
  };

  const removeIconUrl = (index) => {
    setPreviewIconUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (data) => {
    const payload = {
      name: data.name,
      direction: data.direction,
      type: data.type,
      description: data.description,
      icon: data.icon,
      criteria,
      hidden: data.hidden,
      isFeatured: data.isFeatured,
      shouldPreview: data.shouldPreview,
      previewIconUrls
    };

    if (isEditing) {
      await updateSpendingAggregation.mutateAsync({
        id: aggregation.id,
        data: payload
      });
    } else {
      await createSpendingAggregation.mutateAsync({ data: payload });
    }

    onClose?.();
  };

  const handleArchiveToggle = async () => {
    if (aggregation.archived) {
      await restoreSpendingAggregation.mutateAsync({ id: aggregation.id });
    } else {
      await archiveSpendingAggregation.mutateAsync({ id: aggregation.id });
    }
    onClose?.();
  };

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        style: {
          width: 800,
          padding: "24px 32px",
          paddingBottom: "0",
          overflow: "auto"
        }
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)} style={{ height: "100%" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            height: "100%"
          }}
        >
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 500 }}>
            {isEditing ? `Edit: ${aggregation.name}` : "New aggregation"}
          </Typography>

          <Box sx={{ display: "flex", gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <FormInputText name="name" control={control} label="Name *" />
            </Box>
            <Box sx={{ maxWidth: 220, minWidth: 180 }}>
              <FormInputDropdown
                name="direction"
                control={control}
                label="Direction *"
                options={DIRECTION_OPTIONS}
              />
            </Box>
          </Box>

          <Box>
            <Box sx={{ maxWidth: 220 }}>
              <FormInputDropdown
                name="type"
                control={control}
                label="Type"
                options={TYPE_OPTIONS}
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              Auto opt-in: every card-connected user is subscribed and cannot
              unsubscribe.
            </Typography>
          </Box>

          <Box>
            <FormInputText
              name="description"
              control={control}
              label="Description"
              multiline
            />
            <Typography variant="caption" color="text.secondary">
              Curated copy — not user-editable elsewhere.
            </Typography>
          </Box>

          <FormInputText name="icon" control={control} label="Icon" />

          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ mb: -1 }}
          >
            Criteria
          </Typography>
          <CriteriaBuilder criteria={criteria} onChange={setCriteria} />

          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ mb: -1 }}
          >
            Flags
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              px: 2,
              py: 1
            }}
          >
            <Box>
              <Typography variant="body2" fontWeight={500}>
                Hidden
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Curated-but-unlisted — only shown to Commons team or
                already-subscribed users.
              </Typography>
            </Box>
            <Controller
              name="hidden"
              control={control}
              render={({ field }) => (
                <Switch
                  checked={!!field.value}
                  onChange={(event) =>
                    setValue("hidden", event.target.checked)
                  }
                />
              )}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              px: 2,
              py: 1
            }}
          >
            <Box>
              <Typography variant="body2" fontWeight={500}>
                Featured
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Show this watchlist in the featured section on Home when the
                mobile feature is enabled.
              </Typography>
            </Box>
            <Controller
              name="isFeatured"
              control={control}
              render={({ field }) => (
                <Switch
                  checked={!!field.value}
                  onChange={(event) =>
                    setValue("isFeatured", event.target.checked)
                  }
                />
              )}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              px: 2,
              py: 1
            }}
          >
            <Box>
              <Typography variant="body2" fontWeight={500}>
                Should preview
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Client-facing preview flag, surfaced verbatim in the DTO.
              </Typography>
            </Box>
            <Controller
              name="shouldPreview"
              control={control}
              render={({ field }) => (
                <Switch
                  checked={!!field.value}
                  onChange={(event) =>
                    setValue("shouldPreview", event.target.checked)
                  }
                />
              )}
            />
          </Box>

          <Box>
            <Typography variant="body2" fontWeight={500} gutterBottom>
              Preview icon URLs
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1 }}
            >
              Manual override for the preview icon cluster (first 3 used).
              Leave empty to auto-derive from criteria.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
              {previewIconUrls.map((url, index) => (
                <Chip
                  key={url}
                  label={url}
                  onDelete={() => removeIconUrl(index)}
                  variant="outlined"
                />
              ))}
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                size="small"
                label="Add icon URL"
                value={newIconUrl}
                onChange={(event) => setNewIconUrl(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    addIconUrl();
                  }
                }}
                sx={{ flexGrow: 1 }}
              />
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addIconUrl}
                disabled={!newIconUrl.trim()}
              >
                Add URL
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              mt: "auto",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              position: "sticky",
              bottom: -1,
              backgroundColor: "#fff",
              padding: "16px 0"
            }}
          >
            <Box>
              {isEditing && (
                <Button color="error" onClick={handleArchiveToggle}>
                  {aggregation.archived ? "Restore preset" : "Archive preset"}
                </Button>
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant="outlined" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={isSubmitting}
              >
                Save
              </Button>
            </Box>
          </Box>
        </Box>
      </form>
    </Drawer>
  );
};
